/**
 * useForensicRoast V2 — Cache-first, generate-once AI analysis
 *
 * Rules:
 * 1. If verdict.forensicRoast exists → use it. No API call.
 * 2. If missing → call generateForensicRoastV2, persist to verdict doc.
 * 3. Never regenerate on page load. Only on explicit input change.
 * 4. Template fallback if AI fails — user always sees analysis.
 *
 * Replaces: useVerdictNarration.ts, ForensicRoast.tsx fetch logic
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '@/firebase/config';
import { updateVerdict } from '@/services/verdicts';
import type { UserProfile, ForensicRoastData, VerdictInputs, VerdictCalculations, VerdictFlags } from '@/types';

// ── Types ────────────────────────────────────────────────────────────
// ForensicRoastData imported from @/types/verdict

export interface UseForensicRoastReturn {
  analysis: string | null;
  status: string | null;
  isEstimated: boolean;
  isLoading: boolean;
  isFallback: boolean;
}

interface VerdictLike {
  id?: string;
  inputs: VerdictInputs;
  calculations: VerdictCalculations;
  flags?: VerdictFlags;
  forensicRoast?: ForensicRoastData;  // ← NEW dedicated field
  // Legacy fields — read for migration but never write
  aiAnalysis?: { analysis?: string; status?: string; isEstimated?: boolean };
}

interface UseForensicRoastOptions {
  verdict: VerdictLike;
  userId?: string;
  profile: UserProfile | null;
}

// ── Input Hash ───────────────────────────────────────────────────────
// Deterministic hash of the inputs that affect analysis.
// If this changes, the cached analysis is stale.

function computeInputHash(v: VerdictLike): string {
  const key = [
    v.inputs.gutPrice,
    v.inputs.hours,
    v.inputs.materials,
    v.inputs.medium,
    v.inputs.height,
    v.inputs.width,
    v.inputs.tier,
    v.calculations.ttcp,
    v.calculations.delta,
    v.calculations.effectiveHourly,
    v.calculations.survivalTarget,
  ].join('|');

  // Simple fast hash — not crypto, just change detection
  let hash = 0;
  for (let i = 0; i < key.length; i++) {
    hash = ((hash << 5) - hash) + key.charCodeAt(i);
    hash |= 0;
  }
  return hash.toString(36);
}

// ── Template Fallback ────────────────────────────────────────────────

function getTemplateFallback(v: VerdictLike): ForensicRoastData {
  const { inputs, calculations } = v;
  const floor$ = Math.round(calculations.ttcp / 100);
  const gut$ = Math.round((inputs.gutPrice ?? 0) / 100);
  const delta = calculations.delta ?? 0;
  const absDelta$ = Math.round(Math.abs(delta) / 100);
  const ratio = calculations.ttcp > 0 ? delta / calculations.ttcp : 0;

  const status: ForensicRoastData['status'] =
    delta < 0 ? 'Below Floor' :
    (inputs.gutPrice ?? 0) > calculations.ceiling ? 'Above Market Band' :
    'Supported';

  let p1 = `I examined this using your stated hours and costs. Your studio needs **$${floor$.toLocaleString()}** for this work to cover your costs. `;
  let p2 = '';

  if (ratio > 0.50) {
    p1 += `At **$${gut$.toLocaleString()}**, it clears that amount by **$${absDelta$.toLocaleString()}**. You earn well above your survival rate at this price.`;
    p2 = `Materials are a small share of your costs. You have **$${absDelta$.toLocaleString()}** of room above your minimum. This price covers your studio costs.`;
  } else if (ratio >= 0.10) {
    p1 += `At **$${gut$.toLocaleString()}**, it clears your minimum by **$${absDelta$.toLocaleString()}**. Your effective rate is comfortably above what your studio needs.`;
    p2 = `Materials and time are in balance. Hold this price. This price currently supports your studio.`;
  } else if (ratio >= -0.15) {
    const pos = delta >= 0 ? 'barely clears' : 'falls just short of';
    p1 += `At **$${gut$.toLocaleString()}**, it ${pos} your minimum by **$${absDelta$.toLocaleString()}**. You are earning near what your studio needs to survive.`;
    p2 = `Each additional hour tightens the margin further. Small changes in hours or materials will shift this. This price is tight against your costs.`;
  } else if (ratio >= -0.50) {
    p1 += `At **$${gut$.toLocaleString()}**, it falls short by **$${absDelta$.toLocaleString()}**. You are earning below what you need to sustain your studio.`;
    p2 = `Every hour on this piece costs more than it earns. Raise the price to **$${floor$.toLocaleString()}** or cut your hours significantly. This price does not currently support your studio.`;
  } else {
    p1 += `At **$${gut$.toLocaleString()}**, it falls short by **$${absDelta$.toLocaleString()}**, less than half your required minimum. You are earning far below what you need.`;
    p2 = `This many hours at this price cannot work. Raise the price to **$${floor$.toLocaleString()}** or cut your hours significantly. This price does not cover your costs.`;
  }

  return {
    analysis: `${p1}\n\n${p2}`,
    status,
    isEstimated: true,
    generatedAt: new Date().toISOString(),
    inputHash: computeInputHash(v),
  };
}

// ── Hook ─────────────────────────────────────────────────────────────

export function useForensicRoast({
  verdict,
  userId,
  profile,
}: UseForensicRoastOptions): UseForensicRoastReturn {

  // ── Step 1: Check cache ──────────────────────────────────────────
  const currentHash = computeInputHash(verdict);
  const cached = verdict.forensicRoast;
  const cacheValid = cached && cached.inputHash === currentHash;

  // Legacy migration: if no forensicRoast but has old aiAnalysis, use it
  const legacyAnalysis = !cached && verdict.aiAnalysis?.analysis
    ? verdict.aiAnalysis.analysis
    : null;

  const initialAnalysis = cacheValid
    ? cached!.analysis
    : legacyAnalysis;

  const [analysis, setAnalysis] = useState<string | null>(initialAnalysis);
  const [status, setStatus] = useState<string | null>(
    cacheValid ? cached!.status : null
  );
  const [isEstimated, setIsEstimated] = useState(
    cacheValid ? cached!.isEstimated : true
  );
  const [isLoading, setIsLoading] = useState(!initialAnalysis);
  const [isFallback, setIsFallback] = useState(false);
  const hasFetchedRef = useRef(!!initialAnalysis);

  // ── Step 2: Generate if missing or stale ─────────────────────────
  const generate = useCallback(async () => {
    if (hasFetchedRef.current) return;
    hasFetchedRef.current = true;
    setIsLoading(true);

    // No userId → can't call Cloud Function → template fallback
    if (!userId) {
      const fb = getTemplateFallback(verdict);
      setAnalysis(fb.analysis);
      setStatus(fb.status);
      setIsEstimated(true);
      setIsFallback(true);
      setIsLoading(false);
      return;
    }

    try {
      const isPersonalized = profile?.onboardingCompleted &&
        !(profile.monthlyBurn === 312500 && profile.monthlyCapacity === 130);

      const callFn = httpsCallable<
        { verdict: Record<string, unknown>; subscriptionStatus?: string },
        { analysis: string; status: string; isEstimated: boolean } | { error: string }
      >(functions, 'generateForensicRoastV2');

      const result = await callFn({
        verdict: {
          medium: verdict.inputs.medium,
          height: verdict.inputs.height,
          width: verdict.inputs.width,
          hours: verdict.inputs.hours,
          materials: verdict.inputs.materials,
          gutPrice: verdict.inputs.gutPrice ?? 0,
          tier: verdict.inputs.tier,
          floor: verdict.calculations.ttcp,
          ceiling: verdict.calculations.ceiling,
          delta: verdict.calculations.delta ?? 0,
          effRate: verdict.calculations.effectiveHourly ?? 0,
          survivalRate: verdict.calculations.survivalTarget,
          isPersonalized: !!isPersonalized,
          tracked: verdict.inputs.hoursTracked ?? false,
          careerStage: verdict.inputs.careerStage,
          monthlyBurn: verdict.inputs.monthlyBurn,
        },
        subscriptionStatus: profile?.subscriptionStatus ?? 'free',
      });

      const data = result.data;
      if ('error' in data) throw new Error(String(data.error));

      const roastData: ForensicRoastData = {
        analysis: data.analysis,
        status: data.status as ForensicRoastData['status'],
        isEstimated: data.isEstimated,
        generatedAt: new Date().toISOString(),
        inputHash: currentHash,
      };

      setAnalysis(roastData.analysis);
      setStatus(roastData.status);
      setIsEstimated(roastData.isEstimated);

      // ── Step 3: Persist to dedicated field ─────────────────────
      if (verdict.id) {
        try {
          await updateVerdict(userId, verdict.id, {
            forensicRoast: roastData,
          });
        } catch {
          // Cache miss is non-critical — display still works
        }
      }
    } catch {
      // Always show something — template fallback
      const fb = getTemplateFallback(verdict);
      setAnalysis(fb.analysis);
      setStatus(fb.status);
      setIsEstimated(true);
      setIsFallback(true);
    } finally {
      setIsLoading(false);
    }
  }, [verdict, userId, profile, currentHash]);

  useEffect(() => {
    if (initialAnalysis) return; // Cache hit — skip
    const timer = setTimeout(generate, 300);
    return () => clearTimeout(timer);
  }, [generate, initialAnalysis]);

  return { analysis, status, isEstimated, isLoading, isFallback };
}
